import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Plus, Edit, Trash2, Power, PowerOff, RefreshCw, Search, Filter, X } from 'lucide-react';
import { billingService, PricingConfiguration, PricingConfigurationCreateData } from '../../services/billingService';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { PricingConfigurationForm } from '../../components/Billing';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import ConfirmDeleteModal from '../../components/UI/ConfirmDeleteModal';
import { logError } from '../../utils/errorLogger';

const AdminPricingPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  
  const [pricingData, setPricingData] = useState<{
    results: PricingConfiguration[];
    count: number;
    next: string | null;
    previous: string | null;
    page: number;
    total_pages: number;
  } | null>(null);
  const [currentPricing, setCurrentPricing] = useState<PricingConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // 10 configurations per page
  
  // Filter state
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<PricingConfiguration | null>(null);
  
  // Form state
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load pricing configurations
  const loadPricingConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const params: { 
        page: number; 
        page_size: number;
        is_active?: boolean;
        search?: string;
      } = {
        page: currentPage,
        page_size: pageSize
      };
      
      // Add filters
      if (isActiveFilter !== 'all') {
        params.is_active = isActiveFilter === 'active';
      }
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      
      const [data, current] = await Promise.all([
        billingService.getPricingConfigurations(params),
        billingService.getCurrentPricing()
      ]);
      setPricingData({
        results: data.results || [],
        count: data.count || 0,
        next: data.next || null,
        previous: data.previous || null,
        page: currentPage,
        total_pages: Math.ceil((data.count || 0) / pageSize)
      });
      setCurrentPricing(current);
    } catch (error: any) {
      logError('Failed to load pricing configurations', error);
      showNotification('Failed to load pricing configurations', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, isActiveFilter, debouncedSearchTerm, showNotification]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle filter changes
  const handleFilterChange = (filter: 'all' | 'active' | 'inactive') => {
    setIsActiveFilter(filter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Don't reset page here - let debounce handle it
  };

  // Reset page when debounced search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Clear all filters
  const handleClearFilters = () => {
    setIsActiveFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = isActiveFilter !== 'all' || searchTerm.trim() !== '';

  useEffect(() => {
    loadPricingConfigs();
  }, [loadPricingConfigs]);

  // Create pricing configuration
  const handleCreate = async (data: PricingConfigurationCreateData) => {
    try {
      setActionLoading('create');
      setServerErrors({});
      const response = await billingService.createPricingConfiguration(data);
      
      if (response.success) {
        showNotification('Pricing configuration created successfully', 'success');
        setIsCreateModalOpen(false);
        // Reset to first page after creating
        setCurrentPage(1);
        await loadPricingConfigs();
      } else {
        if (response.error?.details) {
          setServerErrors(response.error.details);
        } else {
          showNotification(response.error?.message || 'Failed to create pricing configuration', 'error');
        }
      }
    } catch (error: any) {
      logError('Failed to create pricing configuration', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Check if there are field-specific errors
        if (errorData.details) {
          // Handle __all__ errors (non-field errors)
          const processedErrors: Record<string, string> = {};
          for (const [key, value] of Object.entries(errorData.details)) {
            if (key === '__all__') {
              // Show __all__ errors as a notification
              const message = Array.isArray(value) ? value[0] : value;
              showNotification(String(message), 'error');
            } else {
              // Field-specific errors
              processedErrors[key] = Array.isArray(value) ? value[0] : String(value);
            }
          }
          if (Object.keys(processedErrors).length > 0) {
            setServerErrors(processedErrors);
          }
        } else {
          // Show general error message
          showNotification(errorData.message || 'Failed to create pricing configuration', 'error');
        }
      } else {
        showNotification('Failed to create pricing configuration', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Update pricing configuration
  const handleUpdate = async (data: PricingConfigurationCreateData) => {
    if (!selectedPricing) return;
    
    try {
      setActionLoading('update');
      setServerErrors({});
      const response = await billingService.updatePricingConfiguration(selectedPricing.id, data);
      
      if (response.success) {
        showNotification('Pricing configuration updated successfully', 'success');
        setIsEditModalOpen(false);
        setSelectedPricing(null);
        await loadPricingConfigs();
      } else {
        if (response.error?.details) {
          setServerErrors(response.error.details);
        } else {
          showNotification(response.error?.message || 'Failed to update pricing configuration', 'error');
        }
      }
    } catch (error: any) {
      logError('Failed to update pricing configuration', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Check if there are field-specific errors
        if (errorData.details) {
          // Handle __all__ errors (non-field errors)
          const processedErrors: Record<string, string> = {};
          for (const [key, value] of Object.entries(errorData.details)) {
            if (key === '__all__') {
              // Show __all__ errors as a notification
              const message = Array.isArray(value) ? value[0] : value;
              showNotification(String(message), 'error');
            } else {
              // Field-specific errors
              processedErrors[key] = Array.isArray(value) ? value[0] : String(value);
            }
          }
          if (Object.keys(processedErrors).length > 0) {
            setServerErrors(processedErrors);
          }
        } else {
          // Show general error message
          showNotification(errorData.message || 'Failed to update pricing configuration', 'error');
        }
      } else {
        showNotification('Failed to update pricing configuration', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Deactivate pricing configuration
  const handleDeactivate = async () => {
    if (!selectedPricing) return;
    
    try {
      setActionLoading('deactivate');
      const response = await billingService.deactivatePricingConfiguration(selectedPricing.id);
      
      if (response.success) {
        showNotification('Pricing configuration deactivated successfully', 'success');
        setIsDeleteModalOpen(false);
        setSelectedPricing(null);
        await loadPricingConfigs();
      } else {
        showNotification(response.error?.message || 'Failed to deactivate pricing configuration', 'error');
      }
    } catch (error: any) {
      logError('Failed to deactivate pricing configuration', error);
      showNotification('Failed to deactivate pricing configuration', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-3 sm:px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Pricing Configuration
          </h1>
          <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage pricing settings for the billing system
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={loadPricingConfigs}
            leftIcon={<RefreshCw className="h-4 w-4" />}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setSelectedPricing(null);
              setServerErrors({});
              setIsCreateModalOpen(true);
            }}
            leftIcon={<Plus className="h-4 w-4" />}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <span className="hidden sm:inline">New Configuration</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div
        className={`rounded-lg border p-4 space-y-4 ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Filter className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
            <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Filters & Search
            </h3>
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              leftIcon={<X className="h-4 w-4" />}
              className="self-start sm:self-auto"
            >
              Clear All
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Search
            </label>
            <Input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
            />
          </div>

          {/* Status Filter */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Status
            </label>
            <select
              value={isActiveFilter}
              onChange={(e) => handleFilterChange(e.target.value as 'all' | 'active' | 'inactive')}
              className={`w-full px-3 py-2 rounded-md border-2 transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
                  : 'bg-white border-gray-200 text-gray-900 focus:ring-2 focus:ring-mirsal-green focus:ring-opacity-20 focus:border-mirsal-green'
              }`}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pricing Configurations List */}
      <div className="space-y-4">
        {!pricingData || pricingData.results.length === 0 ? (
          <div className={`rounded-lg border-2 p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="text-center py-12">
              <Settings className={`h-12 w-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                No pricing configurations found
              </p>
              <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                Create your first pricing configuration to get started
              </p>
            </div>
          </div>
        ) : (
          pricingData.results.map((config) => (
            <div key={config.id} className={`rounded-lg border-2 p-4 sm:p-6 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                    <h3 className={`text-base sm:text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {config.name}
                    </h3>
                    {config.is_active && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 w-fit">
                        Active
                      </span>
                    )}
                    {!config.is_active && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400 w-fit">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 text-sm">
                    <div className="flex flex-col sm:block">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Setup:</span>
                      <span className={`sm:ml-2 block sm:inline ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parseFloat(config.setup_fee).toFixed(2)} JOD
                      </span>
                    </div>
                    <div className="flex flex-col sm:block">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Guest:</span>
                      <span className={`sm:ml-2 block sm:inline ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parseFloat(config.guest_base_fee).toFixed(2)} JOD
                      </span>
                    </div>
                    <div className="flex flex-col sm:block">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Companion:</span>
                      <span className={`sm:ml-2 block sm:inline ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parseFloat(config.companion_fee).toFixed(2)} JOD
                      </span>
                    </div>
                    <div className="flex flex-col sm:block">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Image:</span>
                      <span className={`sm:ml-2 block sm:inline ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parseFloat(config.image_message_fee).toFixed(2)} JOD
                      </span>
                    </div>
                    <div className="flex flex-col sm:block">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Save Date:</span>
                      <span className={`sm:ml-2 block sm:inline ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parseFloat(config.save_the_date_fee).toFixed(2)} JOD
                      </span>
                    </div>
                    <div className="flex flex-col sm:block">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Usher Service:</span>
                      <span className={`sm:ml-2 block sm:inline ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parseFloat(config.usher_service_fee).toFixed(2)} JOD
                      </span>
                    </div>
                  </div>
                  {config.created_by_name && (
                    <p className={`text-xs mt-3 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      Created by {config.created_by_name} on {new Date(config.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 lg:flex-col lg:items-end lg:ml-4 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedPricing(config);
                      setServerErrors({});
                      setIsEditModalOpen(true);
                    }}
                    leftIcon={<Edit className="h-4 w-4" />}
                    className="flex-1 sm:flex-initial"
                  >
                    <span className="hidden sm:inline">Edit</span>
                    <span className="sm:hidden">Edit</span>
                  </Button>
                  {config.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedPricing(config);
                        setIsDeleteModalOpen(true);
                      }}
                      leftIcon={<PowerOff className="h-4 w-4" />}
                      className="flex-1 sm:flex-initial"
                    >
                      <span className="hidden sm:inline">Deactivate</span>
                      <span className="sm:hidden">Deactivate</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pricingData && pricingData.count > 0 && (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 rounded-lg border p-4 ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-sm text-center sm:text-left ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {pricingData.results.length} of {pricingData.count} pricing configurations
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pricingData.previous || loading}
              className="flex-1 sm:flex-initial"
            >
              Previous
            </Button>
            <span className={`px-2 sm:px-4 py-2 text-sm whitespace-nowrap ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {currentPage} of {pricingData.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pricingData.next || loading}
              className="flex-1 sm:flex-initial"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setServerErrors({});
        }}
        title="Create Pricing Configuration"
        size="lg"
      >
        <PricingConfigurationForm
          onSubmit={handleCreate}
          onCancel={() => {
            setIsCreateModalOpen(false);
            setServerErrors({});
          }}
          loading={actionLoading === 'create'}
          serverErrors={serverErrors}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPricing(null);
          setServerErrors({});
        }}
        title="Edit Pricing Configuration"
        size="lg"
      >
        {selectedPricing && (
          <PricingConfigurationForm
            pricing={selectedPricing}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedPricing(null);
              setServerErrors({});
            }}
            loading={actionLoading === 'update'}
            serverErrors={serverErrors}
          />
        )}
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPricing(null);
        }}
        onConfirm={handleDeactivate}
        title="Deactivate Pricing Configuration"
        message={`Are you sure you want to deactivate "${selectedPricing?.name}"? This will make it inactive, but you can reactivate it later.`}
        itemName={selectedPricing?.name || ''}
        itemType="pricing configuration"
        loading={actionLoading === 'deactivate'}
        destructive={false}
      />
    </div>
  );
};

export default AdminPricingPage;

